import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AlertCircle, Pause, Eye, LockKeyhole, Search, ArrowUpDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Type2BotPerformance } from '@/services/conditional-skip-stats-service';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Type2BotDetailDialog } from './Type2BotDetailDialog';

interface BotPerformanceTableProps {
  bots: Type2BotPerformance[];
  timeRange: string;
  onRefresh: () => void;
}

type SortOption = 'total_signals' | 'win_rate_high' | 'win_rate_low' | 'improvement_high' | 'improvement_low';

export const BotPerformanceTable: React.FC<BotPerformanceTableProps> = ({ bots, timeRange, onRefresh }) => {
  const [pausingBotId, setPausingBotId] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<Type2BotPerformance | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('total_signals');
  const isMobile = useIsMobile();

  // Filter and sort bots
  const filteredAndSortedBots = useMemo(() => {
    let filtered = bots.filter(bot => 
      bot.bot_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'win_rate_high':
          return b.win_rate - a.win_rate;
        case 'win_rate_low':
          return a.win_rate - b.win_rate;
        case 'improvement_high':
          return b.win_rate_improvement_pts - a.win_rate_improvement_pts;
        case 'improvement_low':
          return a.win_rate_improvement_pts - b.win_rate_improvement_pts;
        case 'total_signals':
        default:
          return b.total_signals - a.total_signals;
      }
    });
  }, [bots, searchTerm, sortBy]);

  const handlePauseBot = async (botId: string) => {
    try {
      // Update bot_accounts status to paused
      const { error } = await supabase
        .from('bot_accounts')
        .update({ status: 'paused' })
        .eq('id', botId);

      if (error) throw error;

      toast.success('Bot đã được tạm dừng');
      setPausingBotId(null);
      onRefresh();
    } catch (error) {
      console.error('Error pausing bot:', error);
      toast.error('Không thể tạm dừng bot');
    }
  };

  const getWinRateColor = (winRate: number, totalTrades: number) => {
    if (totalTrades === 0) return 'text-muted-foreground';
    if (winRate >= 60) return 'text-green-500 font-semibold';
    if (winRate >= 50) return 'text-orange-400';
    return 'text-red-500 font-semibold';
  };

  const getForwardRateColor = (forwardRate: number) => {
    if (forwardRate >= 80) return 'text-blue-600';
    if (forwardRate >= 50) return 'text-blue-500';
    return 'text-blue-400';
  };

  const getImprovementColor = (improvement: number) => {
    if (improvement > 5) return 'text-green-600 font-semibold';
    if (improvement < -5) return 'text-red-600 font-semibold';
    return 'text-yellow-600';
  };

  const getImprovementSymbol = (improvement: number) => {
    if (improvement > 5) return '↑';
    if (improvement < -5) return '↓';
    return '~';
  };

  if (bots.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Chưa có dữ liệu bot Type 2
      </div>
    );
  }

  // Toolbar for search and sort
  const Toolbar = () => (
    <div className={`flex flex-col ${isMobile ? "gap-3 mb-4" : "sm:flex-row gap-4 mb-6"}`}>
      <div className="relative flex-1">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground ${isMobile ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
        <Input
          placeholder="Tìm bot theo tên..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`${isMobile ? "pl-8 h-9 text-sm" : "pl-9"}`}
        />
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={sortBy === 'total_signals' ? 'default' : 'outline'}
          onClick={() => setSortBy('total_signals')}
          size={isMobile ? "sm" : "default"}
          className={isMobile ? "text-xs" : ""}
        >
          Tổng Signals
        </Button>
        <Button
          variant={sortBy === 'win_rate_high' ? 'default' : 'outline'}
          onClick={() => setSortBy('win_rate_high')}
          size={isMobile ? "sm" : "default"}
          className={isMobile ? "text-xs" : ""}
        >
          WR Cao→Thấp
        </Button>
        <Button
          variant={sortBy === 'win_rate_low' ? 'default' : 'outline'}
          onClick={() => setSortBy('win_rate_low')}
          size={isMobile ? "sm" : "default"}
          className={isMobile ? "text-xs" : ""}
        >
          WR Thấp→Cao
        </Button>
        <Button
          variant={sortBy === 'improvement_high' ? 'default' : 'outline'}
          onClick={() => setSortBy('improvement_high')}
          size={isMobile ? "sm" : "default"}
          className={isMobile ? "text-xs" : ""}
        >
          Cải Thiện Cao
        </Button>
      </div>
    </div>
  );

  // Mobile card view
  if (isMobile) {
    return (
      <>
        <Toolbar />
        {filteredAndSortedBots.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Không tìm thấy bot phù hợp
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAndSortedBots.map((bot) => (
              <Card key={bot.bot_id} className={bot.is_permanent_locked ? 'border-red-500 bg-red-50' : ''}>
                <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm">{bot.bot_name}</h4>
                      {bot.is_permanent_locked && (
                        <LockKeyhole className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Badge variant={bot.is_active ? "default" : "secondary"}>
                        {bot.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {bot.is_permanent_locked && (
                        <Badge variant="destructive" className="text-xs">
                          🔒 Lock: {bot.lock_pattern}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setSelectedBot(bot)}
                      className="min-h-[44px] min-w-[44px]"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {bot.is_active && bot.win_rate < 40 && bot.trades >= 10 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPausingBotId(bot.bot_id)}
                        className="min-h-[44px] min-w-[44px]"
                      >
                        <Pause className="h-4 w-4 text-orange-600" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">WR (All)</p>
                    <p className="text-muted-foreground">
                      {bot.all_signals_trades > 0 
                        ? `${bot.all_signals_win_rate.toFixed(1)}%`
                        : '-'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">WR (Filtered)</p>
                    <p className={getWinRateColor(bot.win_rate, bot.trades)}>
                      {bot.trades > 0 ? (
                        <>
                          {bot.win_rate.toFixed(1)}%
                          {bot.win_rate < 40 && bot.trades >= 10 && (
                            <AlertCircle className="inline h-3 w-3 ml-1" />
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-orange-500">No Forward</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Improvement</p>
                    <p className={getImprovementColor(bot.win_rate_improvement_pts)}>
                      {bot.trades > 0 && bot.all_signals_trades > 0
                        ? `${bot.win_rate_improvement_pts > 0 ? '+' : ''}${bot.win_rate_improvement_pts.toFixed(1)} pts ${getImprovementSymbol(bot.win_rate_improvement_pts)}`
                        : '-'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Trades</p>
                    <p>
                      <span className="text-green-600">{bot.wins}</span>
                      <span className="text-muted-foreground mx-1">/</span>
                      <span className="text-red-600">{bot.losses}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Forward Rate</p>
                    <p className={getForwardRateColor(bot.forward_rate)}>
                      {bot.forward_rate.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Total Signals</p>
                    <p>{bot.total_signals}</p>
                  </div>
                </div>
                
                <div className="mt-3 text-xs text-muted-foreground">
                  Last signal: {bot.last_signal_at 
                    ? formatDistanceToNow(new Date(bot.last_signal_at), { addSuffix: true })
                    : 'Never'}
                </div>
              </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialogs */}
        <AlertDialog open={pausingBotId !== null} onOpenChange={() => setPausingBotId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tạm dừng bot?</AlertDialogTitle>
              <AlertDialogDescription>
                Bạn có chắc chắn muốn tạm dừng bot này? Bot sẽ không nhận signal mới cho đến khi được kích hoạt lại.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Hủy</AlertDialogCancel>
              <AlertDialogAction onClick={() => pausingBotId && handlePauseBot(pausingBotId)}>
                Tạm dừng
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Type2BotDetailDialog
          bot={selectedBot}
          timeRange={timeRange}
          open={selectedBot !== null}
          onOpenChange={(open) => !open && setSelectedBot(null)}
          onRefresh={onRefresh}
        />
      </>
    );
  }

  // Desktop table view
  return (
    <>
      <Toolbar />
      {filteredAndSortedBots.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Không tìm thấy bot phù hợp
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bot Name</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">WR (All)</TableHead>
              <TableHead className="text-right">WR (Filtered)</TableHead>
              <TableHead className="text-right">Δ</TableHead>
              <TableHead className="text-right">Trades</TableHead>
              <TableHead className="text-right">Forward Rate</TableHead>
              <TableHead className="text-right">Total Signals</TableHead>
              <TableHead className="text-right">Last Signal</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedBots.map((bot) => (
                <TableRow key={bot.bot_id} className={bot.is_permanent_locked ? 'bg-red-50' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {bot.bot_name}
                      {bot.is_permanent_locked && (
                        <Badge variant="destructive" className="text-[10px] gap-1">
                          <LockKeyhole className="h-3 w-3" />
                          PERMANENT: {bot.lock_pattern}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={bot.is_active ? "default" : "secondary"}>
                      {bot.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {bot.all_signals_trades > 0 
                      ? `${bot.all_signals_win_rate.toFixed(1)}%`
                      : '-'
                    }
                  </TableCell>
                  <TableCell className={`text-right ${getWinRateColor(bot.win_rate, bot.trades)}`}>
                    {bot.trades > 0 ? (
                      <>
                        {bot.win_rate.toFixed(1)}%
                        {bot.win_rate < 40 && bot.trades >= 10 && (
                          <AlertCircle className="inline h-3 w-3 ml-1" />
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-orange-500">No Forward</span>
                    )}
                  </TableCell>
                  <TableCell className={`text-right ${getImprovementColor(bot.win_rate_improvement_pts)}`}>
                    {bot.trades > 0 && bot.all_signals_trades > 0
                      ? `${bot.win_rate_improvement_pts > 0 ? '+' : ''}${bot.win_rate_improvement_pts.toFixed(1)} ${getImprovementSymbol(bot.win_rate_improvement_pts)}`
                      : '-'
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-green-600">{bot.wins}</span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span className="text-red-600">{bot.losses}</span>
                  </TableCell>
                  <TableCell className={`text-right ${getForwardRateColor(bot.forward_rate)}`}>
                    {bot.forward_rate.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">{bot.total_signals}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {bot.last_signal_at 
                      ? formatDistanceToNow(new Date(bot.last_signal_at), { addSuffix: true })
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedBot(bot)}
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      {bot.is_active && bot.win_rate < 40 && bot.trades >= 10 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPausingBotId(bot.bot_id)}
                        >
                          <Pause className="h-3 w-3 text-orange-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
              </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pause Confirmation Dialog */}
      <AlertDialog open={pausingBotId !== null} onOpenChange={() => setPausingBotId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tạm dừng bot?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn tạm dừng bot này? Bot sẽ không nhận signal mới cho đến khi được kích hoạt lại.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={() => pausingBotId && handlePauseBot(pausingBotId)}>
              Tạm dừng
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bot Detail Dialog */}
      <Type2BotDetailDialog
        bot={selectedBot}
        timeRange={timeRange}
        open={selectedBot !== null}
        onOpenChange={(open) => !open && setSelectedBot(null)}
        onRefresh={onRefresh}
      />
    </>
  );
};
